import type { CallTaskEventId } from "@repo/app-types/entities/callTaskEvent";
import type { RecordingId } from "@repo/app-types/entities/recording";
import {
  buildCallTitle,
  type CallOutlineSegment,
  type TeammateAccountCallOutline,
} from "@repo/app-types/entities/teammateAccountCallOutline";
import type {
  AccountGradingRecording,
  CustomerInteractionTextThread,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { Seconds } from "@repo/app-types/units/duration";
import { buildGradingTranscriptItems } from "./gradingTranscriptItems.js";

interface CallNavigationItem {
  type: "call";
  id: RecordingId;
  startedAt: string;
  duration: Seconds;
  callNumber: number;
  title: string;
  segments: readonly CallOutlineSegment[];
}

export interface TextConversationNavigationItem {
  type: "textConversation";
  /** The conversation's first text, where it opens. */
  id: CallTaskEventId;
  startedAt: string;
  textMessageIds: readonly CallTaskEventId[];
}

type TranscriptNavigationItem =
  | CallNavigationItem
  | TextConversationNavigationItem;

export interface CallPosition {
  type: "call";
  recordingId: RecordingId;
  timestamp: number;
  /** Exact transcript fragment for positions resolved from call navigation. */
  fragmentIndex?: number;
}

interface TextMessagePosition {
  type: "textMessage";
  textMessageId: CallTaskEventId;
}

export type TranscriptPosition = CallPosition | TextMessagePosition;

export function checkCallSegmentContainsPosition(
  recordingId: RecordingId,
  segment: CallOutlineSegment,
  position: TranscriptPosition | undefined,
): boolean {
  return (
    position?.type === "call" &&
    position.recordingId === recordingId &&
    position.timestamp >= segment.start_timestamp &&
    position.timestamp < segment.end_timestamp
  );
}

function buildCallNavigationItems(
  recordings: readonly AccountGradingRecording[],
  callOutline: TeammateAccountCallOutline | undefined,
): CallNavigationItem[] {
  const outlinesByRecordingId = new Map(
    callOutline?.calls.map((call) => [call.recording_id, call]) ?? [],
  );

  return recordings.map((recording, index) => {
    const outline = outlinesByRecordingId.get(recording.id);
    return {
      type: "call",
      id: recording.id,
      startedAt: recording.recorded_at,
      duration: recording.duration,
      callNumber: index + 1,
      title: buildCallTitle(outline, recording.title) || `Call ${index + 1}`,
      segments: outline?.segments ?? [],
    };
  });
}

/** Calls and text conversations, split and ordered as the transcript shows them. */
export function buildTranscriptNavigationItems(
  recordings: readonly AccountGradingRecording[],
  callOutline: TeammateAccountCallOutline | undefined,
  textThread: CustomerInteractionTextThread | undefined,
): TranscriptNavigationItem[] {
  const calls = buildCallNavigationItems(recordings, callOutline);
  const callsById = new Map(calls.map((call) => [call.id, call]));
  const items = buildGradingTranscriptItems(
    recordings,
    textThread?.texts ?? [],
    textThread?.agent_steps ?? [],
  );

  return items.flatMap((item): TranscriptNavigationItem[] => {
    if (item.type === "call") {
      const call = callsById.get(item.recording.id);
      return call ? [call] : [];
    }
    return item.type === "texts"
      ? [
          {
            type: "textConversation",
            id: item.firstText.id,
            startedAt: item.firstText.at,
            textMessageIds: item.entries.flatMap((entry) =>
              entry.type === "text" ? [entry.text.id] : [],
            ),
          },
        ]
      : [];
  });
}

/** Names what the items hold, such as "Calls and text conversations". */
export function describeTranscriptNavigationItems(
  items: readonly TranscriptNavigationItem[],
): string {
  const hasCalls = items.some((item) => item.type === "call");
  const hasTextConversations = items.some(
    (item) => item.type === "textConversation",
  );
  if (hasCalls && hasTextConversations) return "Calls and text conversations";
  return hasTextConversations ? "Text conversations" : "Calls";
}
